import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from './UserContext';
import { API_BASE } from '../config/api';

export interface Playlist {
    id: number;
    name: string;
    description: string;
    creatorId: number;
}

export interface PlaylistWithTracks extends Playlist {
    trackIds: number[];
}

interface PlaylistApiResponse {
    id: number;
    name: string;
    description: string;
    creator_id: number;
}

interface PlaylistWithTracksApiResponse extends PlaylistApiResponse {
    track_ids: number[];
}

function mapPlaylist(data: PlaylistApiResponse): Playlist {
    return { id: data.id, name: data.name, description: data.description, creatorId: data.creator_id };
}

function mapPlaylistWithTracks(data: PlaylistWithTracksApiResponse): PlaylistWithTracks {
    return { ...mapPlaylist(data), trackIds: data.track_ids };
}

interface PlaylistsContextType {
    playlists: Playlist[];
    loading: boolean;
    refreshPlaylists: () => Promise<void>;
    createPlaylist: (name: string, description: string) => Promise<Playlist | null>;
    updatePlaylist: (id: number, updates: { name?: string; description?: string }) => Promise<Playlist | null>;
    deletePlaylist: (id: number) => Promise<boolean>;
    getPlaylistWithTracks: (id: number) => Promise<PlaylistWithTracks | null>;
    setPlaylistTracks: (id: number, trackIds: number[]) => Promise<PlaylistWithTracks | null>;
    addTrackToPlaylist: (playlistId: number, trackId: number) => Promise<boolean>;
    addTracksToPlaylist: (playlistId: number, trackIds: number[]) => Promise<boolean>;
}

export const PlaylistsContext = createContext<PlaylistsContextType>({
    playlists: [],
    loading: true,
    refreshPlaylists: async () => {},
    createPlaylist: async () => null,
    updatePlaylist: async () => null,
    deletePlaylist: async () => false,
    getPlaylistWithTracks: async () => null,
    setPlaylistTracks: async () => null,
    addTrackToPlaylist: async () => false,
    addTracksToPlaylist: async () => false,
});

export const PlaylistsProvider = ({ children }: { children: React.ReactNode }) => {
    const { user } = useContext(UserContext);
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [loading, setLoading] = useState(true);

    const refreshPlaylists = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/playlist`, { credentials: 'include' });
            if (response.ok) {
                const data: PlaylistApiResponse[] = await response.json();
                setPlaylists(data.map(mapPlaylist));
            }
        } catch {
            // Keep whatever was already loaded - the grid just won't refresh this time.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) {
            refreshPlaylists();
        } else {
            setPlaylists([]);
            setLoading(false);
        }
    }, [user, refreshPlaylists]);

    const createPlaylist = useCallback(async (name: string, description: string): Promise<Playlist | null> => {
        const response = await fetch(`${API_BASE}/api/playlist`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, description }),
        });
        if (!response.ok) return null;
        const playlist = mapPlaylist(await response.json());
        setPlaylists((prev) => [...prev, playlist]);
        return playlist;
    }, []);

    const updatePlaylist = useCallback(
        async (id: number, updates: { name?: string; description?: string }): Promise<Playlist | null> => {
            const response = await fetch(`${API_BASE}/api/playlist?id=${id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            if (!response.ok) return null;
            const playlist = mapPlaylist(await response.json());
            setPlaylists((prev) => prev.map((p) => (p.id === id ? playlist : p)));
            return playlist;
        },
        []
    );

    const deletePlaylist = useCallback(async (id: number): Promise<boolean> => {
        const response = await fetch(`${API_BASE}/api/playlist?id=${id}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) return false;
        setPlaylists((prev) => prev.filter((p) => p.id !== id));
        return true;
    }, []);

    const getPlaylistWithTracks = useCallback(async (id: number): Promise<PlaylistWithTracks | null> => {
        const response = await fetch(`${API_BASE}/api/playlist?id=${id}`, { credentials: 'include' });
        if (!response.ok) return null;
        return mapPlaylistWithTracks(await response.json());
    }, []);

    const setPlaylistTracks = useCallback(async (id: number, trackIds: number[]): Promise<PlaylistWithTracks | null> => {
        const response = await fetch(`${API_BASE}/api/playlist/tracks?id=${id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ track_ids: trackIds }),
        });
        if (!response.ok) return null;
        return mapPlaylistWithTracks(await response.json());
    }, []);

    // Appends a single track to a playlist. There's no dedicated "add one"
    // endpoint - only the consolidated replace-list PUT - so this fetches
    // the playlist's current tracks, appends, and writes the whole list
    // back. Duplicates are allowed by design, so no membership check first.
    const addTrackToPlaylist = useCallback(
        async (playlistId: number, trackId: number): Promise<boolean> => {
            const current = await getPlaylistWithTracks(playlistId);
            if (!current) return false;
            const result = await setPlaylistTracks(playlistId, [...current.trackIds, trackId]);
            return Boolean(result);
        },
        [getPlaylistWithTracks, setPlaylistTracks]
    );

    // Bulk version of addTrackToPlaylist - same fetch-append-write pattern,
    // one round trip for the whole set of tracks instead of one per track.
    const addTracksToPlaylist = useCallback(
        async (playlistId: number, trackIds: number[]): Promise<boolean> => {
            if (trackIds.length === 0) return true;
            const current = await getPlaylistWithTracks(playlistId);
            if (!current) return false;
            const result = await setPlaylistTracks(playlistId, [...current.trackIds, ...trackIds]);
            return Boolean(result);
        },
        [getPlaylistWithTracks, setPlaylistTracks]
    );

    return (
        <PlaylistsContext.Provider
            value={{
                playlists,
                loading,
                refreshPlaylists,
                createPlaylist,
                updatePlaylist,
                deletePlaylist,
                getPlaylistWithTracks,
                setPlaylistTracks,
                addTrackToPlaylist,
                addTracksToPlaylist,
            }}
        >
            {children}
        </PlaylistsContext.Provider>
    );
};
